import { useMemo, useState } from "react";
import { useSafeFetch } from "../../lib/useSafeFetch";
import { ChevronLeft, ChevronRight, Calendar as CalIcon, ArrowUp, ArrowDown, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const STATUS_TINT = {
  confirmed:       "bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-800",
  pending_payment: "bg-amber-100  text-amber-900  border-amber-300  dark:bg-amber-900/40  dark:text-amber-200  dark:border-amber-800",
  pending: "bg-sky-100 text-sky-900 border-sky-300 dark:bg-sky-900/40 dark:text-sky-200 dark:border-sky-800",
  cancelled:       "bg-rose-100   text-rose-900   border-rose-300   dark:bg-rose-900/40   dark:text-rose-200   dark:border-rose-800",
  completed:       "bg-neutral-200 text-neutral-900 border-neutral-300 dark:bg-neutral-700 dark:text-neutral-200 dark:border-neutral-600",
};

const DAY_LABEL = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function mondayOf(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();              // 0..6 Sun..Sat
  const diff = (day + 6) % 7;          // days since Monday
  d.setDate(d.getDate() - diff);
  return d;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function iso(d) {
  return d.toISOString().slice(0, 10);
}

export default function AdminCalendar() {
  const [mode, setMode] = useState("week");   // "week" | "month"
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));

  const spanDays = mode === "month" ? 30 : 7;
  const weekDays = useMemo(
    () => Array.from({ length: spanDays }, (_, i) => addDays(weekStart, i)),
    [weekStart, spanDays]
  );
  const weekEndIso = iso(addDays(weekStart, spanDays));
  const weekStartIso = iso(weekStart);

  // v4.6.6 — approve a booking request straight from the calendar chip.
  const approveRequest = async (b) => {
    try {
      await api.post(`/admin/bookings/${b.id}/approve-request`);
      toast.success("Request approved — confirmation email sent.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  // Server response envelope is `{results:[...]}` — useSafeFetch already
  // knows how to unwrap that shape when asList:true.
  const { data: bookings, loading, error, refetch: load } = useSafeFetch(
    "/admin/bookings",
    {
      defaultValue: [], asList: true,
      params: {
        pickup_date_from: weekStartIso,
        pickup_date_to: weekEndIso,
        limit: 500,
      },
    },
  );

  // Group bookings by vehicle. Each booking spans pickup_date..return_date.
  const rows = useMemo(() => {
    const byVehicle = new Map();
    for (const b of bookings) {
      const v = b.vehicle_snapshot || {};
      const key = b.vehicle_id || `${v.brand}-${v.name}`;
      if (!byVehicle.has(key)) {
        byVehicle.set(key, { vehicle_id: b.vehicle_id, label: `${v.brand || ""} ${v.name || ""}`.trim() || "Unknown", image: v.image_url, bookings: [] });
      }
      byVehicle.get(key).bookings.push(b);
    }
    return Array.from(byVehicle.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [bookings]);

  const todayIso = iso(new Date());

  return (
    <div data-testid="admin-calendar-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Operations</p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">
            Booking <em className="italic">calendar</em>.
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2">
            Pickups (<ArrowUp size={11} className="inline text-sky-600" />) and returns (<ArrowDown size={11} className="inline text-rose-600" />) by day. Click a chip to jump to the booking.
          </p>
        </div>
        <div className="flex items-center gap-2 bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 p-1 rounded">
          <button
            onClick={() => setWeekStart((d) => addDays(d, -7))}
            className="p-2 hover:bg-black/5 dark:hover:bg-white/5 rounded"
            aria-label="Previous week"
            data-testid="cal-prev"
          ><ChevronLeft size={16} /></button>
          <button
            onClick={() => setWeekStart((d) => addDays(d, -spanDays))}
            className="p-2 hover:bg-black/5 dark:hover:bg-white/5 rounded"
            aria-label={mode === "month" ? "Previous month" : "Previous week"}
            data-testid="cal-prev"
          ><ChevronLeft size={16} /></button>
          <button
            onClick={() => setWeekStart(mondayOf(new Date()))}
            className="px-3 py-1.5 text-xs hover:bg-black/5 dark:hover:bg-white/5 rounded inline-flex items-center gap-1.5"
            data-testid="cal-today"
          ><CalIcon size={12} /> Today</button>
          <button
            onClick={() => setWeekStart((d) => addDays(d, spanDays))}
            className="p-2 hover:bg-black/5 dark:hover:bg-white/5 rounded"
            aria-label={mode === "month" ? "Next month" : "Next week"}
            data-testid="cal-next"
          ><ChevronRight size={16} /></button>
          <div className="ml-4 inline-flex rounded border border-black/10 dark:border-white/10 overflow-hidden" data-testid="cal-view-toggle">
            {["week", "month"].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                data-testid={`cal-view-${m}`}
                className={`px-3 py-1 text-xs uppercase tracking-widest transition-colors ${
                  mode === m
                    ? "bg-black text-white dark:bg-white dark:text-black"
                    : "hover:bg-black/5 dark:hover:bg-white/5 text-neutral-600 dark:text-neutral-300"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && <p className="text-red-600 dark:text-rose-400 text-sm mb-4" data-testid="cal-error">{error}</p>}

      <div className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded-lg overflow-x-auto">
        {/* Header row: dynamic — 7 cols in week view, 30 in month view */}
        <div
          className="grid border-b border-black/8 dark:border-white/10 bg-[#FAF9F6] dark:bg-[#111827]"
          style={{ gridTemplateColumns: `180px repeat(${spanDays}, ${mode === "month" ? "44px" : "minmax(0,1fr)"})`, minWidth: mode === "month" ? "1500px" : "auto" }}
        >
          <div className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-neutral-500 dark:text-neutral-400">Vehicle</div>
          {weekDays.map((d, i) => {
            const ds = iso(d);
            const isToday = ds === todayIso;
            const dayLabel = mode === "week" ? DAY_LABEL[i] : d.toLocaleString("en-GB", { month: "short" }).toUpperCase();
            return (
              <div
                key={ds}
                className={`px-1 py-3 text-center border-l border-black/8 dark:border-white/10 ${isToday ? "bg-black/5 dark:bg-white/5" : ""}`}
                data-testid={`cal-header-${ds}`}
              >
                <div className="text-[9px] uppercase tracking-[0.15em] text-neutral-500 dark:text-neutral-400">{dayLabel}</div>
                <div className={`font-display text-sm mt-0.5 ${isToday ? "text-black dark:text-white" : ""}`}>{d.getDate()}</div>
              </div>
            );
          })}
        </div>

        {/* Body */}
        {loading && <div className="p-8 text-center text-neutral-500">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="p-12 text-center text-neutral-500 dark:text-neutral-400">
            No bookings in this range. Use ← / → to navigate.
          </div>
        )}
        {!loading && rows.map((row) => (
          <div
            key={row.vehicle_id || row.label}
            className="grid border-b border-black/5 dark:border-white/5 last:border-b-0"
            style={{ gridTemplateColumns: `180px repeat(${spanDays}, ${mode === "month" ? "44px" : "minmax(0,1fr)"})`, minWidth: mode === "month" ? "1500px" : "auto" }}
            data-testid={`cal-row-${row.vehicle_id || row.label}`}
          >
            <div className="px-4 py-3 flex items-center gap-3 min-w-0">
              {row.image && <img src={row.image} alt="" className="w-10 h-7 object-cover rounded" />}
              <div className="text-sm truncate">{row.label}</div>
            </div>
            {weekDays.map((d) => {
              const ds = iso(d);
              const isToday = ds === todayIso;
              const todays = row.bookings.filter(
                (b) => b.pickup_date === ds || b.return_date === ds ||
                       (b.pickup_date < ds && b.return_date > ds)
              );
              return (
                <div
                  key={ds}
                  className={`px-1.5 py-1.5 border-l border-black/8 dark:border-white/10 min-h-[64px] ${isToday ? "bg-black/[0.02] dark:bg-white/[0.03]" : ""}`}
                  data-testid={`cal-cell-${row.vehicle_id || row.label}-${ds}`}
                >
                  {todays.map((b) => {
                    const isPickup = b.pickup_date === ds;
                    const isReturn = b.return_date === ds;
                    return (
                      <div
                        key={b.id}
                        title={`${b.full_name} · ${b.pickup_date} → ${b.return_date}`}
                        className={`mb-1 last:mb-0 px-2 py-1 text-[10px] rounded border ${STATUS_TINT[b.status] || ""} truncate flex items-center gap-1`}
                        data-testid={`cal-chip-${b.id}`}
                      >
                        {isPickup && <ArrowUp size={9} />}
                        {isReturn && <ArrowDown size={9} />}
                        <span className="truncate">{b.full_name}</span>
                        {b.status === "pending" && (
                          <button
                            type="button"
                            title="Approve booking request"
                            onClick={() => approveRequest(b)}
                            className="ml-auto shrink-0 text-emerald-700 hover:text-emerald-900 dark:text-emerald-300"
                            data-testid={`cal-approve-${b.id}`}
                          >
                            <Check size={10} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
